import {
  Layout as RALayout,
  CheckForApplicationUpdate,
  Sidebar,
  Menu,
  DashboardMenuItem,
  MenuItemLink,
  useSidebarState,
  AppBar,
  UserMenu,
  Logout,
  useGetIdentity
} from "react-admin";
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import GavelIcon from '@mui/icons-material/Gavel';
import SearchIcon from '@mui/icons-material/Search';
import ReportIcon from '@mui/icons-material/Report';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import SellIcon from '@mui/icons-material/Sell';
import RuleIcon from '@mui/icons-material/Rule';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BarChartIcon from '@mui/icons-material/BarChart';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TuneIcon from '@mui/icons-material/Tune';
import { ReactNode, useMemo, useState } from "react";
import { AppPage, AppRole, canAccessPage } from "./accessControl";

type MenuSectionKey = 'moderation' | 'search' | 'analytics' | 'admin';

type MenuEntry = {
  page: AppPage;
  to: string;
  primaryText: string;
  leftIcon: ReactNode;
};

const MODERATION_ENTRIES: MenuEntry[] = [
  { page: 'reviews', to: '/reviews', primaryText: 'Отзывы', leftIcon: <GavelIcon /> },
  { page: 'adsModeration', to: '/adsModeration', primaryText: 'Объявления', leftIcon: <GavelIcon /> },
  { page: 'complaints', to: '/complaints', primaryText: 'Жалобы пользователей', leftIcon: <ReportIcon /> },
  { page: 'adsExpertModeration', to: '/adsExpertModeration', primaryText: 'Экспертная модерация', leftIcon: <SearchIcon /> }
];

const SEARCH_ENTRIES: MenuEntry[] = [
  { page: 'orders', to: '/orders', primaryText: 'Заказы', leftIcon: <ShoppingCartIcon /> },
  { page: 'users', to: '/users', primaryText: 'Пользователи', leftIcon: <PersonSearchIcon /> },
  { page: 'userAdInfo', to: '/userAds', primaryText: 'Объявления', leftIcon: <SellIcon /> }
];

const ADMIN_ENTRIES: MenuEntry[] = [
  { page: 'projectSettings', to: '/project-settings', primaryText: 'Проект', leftIcon: <SettingsApplicationsIcon /> },
  { page: 'customReasons', to: '/customReasons', primaryText: 'Причины отказа', leftIcon: <RuleIcon /> },
  { page: 'promoCodes', to: '/promoCodes', primaryText: 'Промокоды', leftIcon: <LocalOfferIcon /> },
  { page: 'remoteConfig', to: '/remote-config', primaryText: 'Remote Config', leftIcon: <TuneIcon /> }
];

const ANALYTICS_ENTRIES: MenuEntry[] = [
  { page: 'expertStatistics', to: '/expertStatistics', primaryText: 'Статистика', leftIcon: <BarChartIcon /> },
  { page: 'expertPersonalStatistics', to: '/expertPersonalStatistics', primaryText: 'Моя статистика эксперта', leftIcon: <BarChartIcon /> },
  { page: 'moderatorPersonalStatistics', to: '/moderatorPersonalStatistics', primaryText: 'Моя статистика модератора', leftIcon: <BarChartIcon /> },
  { page: 'moderatorStatistics', to: '/moderatorStatistics', primaryText: 'Статистика команды', leftIcon: <BarChartIcon /> },
  { page: 'sellerDeficits', to: '/sellerDeficits', primaryText: 'Дефициты', leftIcon: <BarChartIcon /> }
];

const menuItemSx = {
  pl: 4,
  alignItems: 'center',
  whiteSpace: 'normal',
  '& .MuiListItemIcon-root': { minWidth: 40 }
};

const CustomMenu = (props: any) => {
  const { identity } = useGetIdentity();
  const currentUser = (identity ?? null) as { role?: AppRole } | null;
  const role = currentUser?.role;
  const [sidebarOpen] = useSidebarState();
  const [openSections, setOpenSections] = useState<Record<MenuSectionKey, boolean>>({
    moderation: true,
    search: true,
    analytics: true,
    admin: true
  });

  const moderationEntries = useMemo(
    () => MODERATION_ENTRIES.filter((entry) => canAccessPage(role, entry.page)),
    [role]
  );
  const searchEntries = useMemo(
    () => SEARCH_ENTRIES.filter((entry) => canAccessPage(role, entry.page)),
    [role]
  );
  const adminEntries = useMemo(
    () => ADMIN_ENTRIES.filter((entry) => canAccessPage(role, entry.page)),
    [role]
  );
  const analyticsEntries = useMemo(
    () => ANALYTICS_ENTRIES.filter((entry) => canAccessPage(role, entry.page)),
    [role]
  );

  const handleToggle = (key: MenuSectionKey) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderSectionHeader = (
    key: MenuSectionKey,
    label: string,
    icon: ReactNode,
  ) => {
    const button = (
      <ListItemButton
        onClick={() => handleToggle(key)}
        sx={{
          alignItems: 'center',
          '& .MuiListItemIcon-root': { minWidth: 40 },
          '& .MuiListItemText-root': { my: 0, whiteSpace: 'normal' },
        }}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{ whiteSpace: 'normal', lineHeight: 1.2 }}
        />
        {openSections[key] ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
    );

    if (sidebarOpen) {
      return button;
    }

    return (
      <Tooltip title={label} placement="right">
        {button}
      </Tooltip>
    );
  };

  const renderEntries = (entries: MenuEntry[]) => (
    entries.map((entry) => (
      <MenuItemLink
        key={entry.to}
        to={entry.to}
        primaryText={entry.primaryText}
        leftIcon={entry.leftIcon}
        sx={menuItemSx}
      />
    ))
  );

  return (
    <Menu {...props}>
      <DashboardMenuItem />
      <Divider sx={{ my: 1 }} />

      {moderationEntries.length > 0 ? (
        <>
          {renderSectionHeader('moderation', 'Модерация контента', <GavelIcon />)}
          <Collapse in={sidebarOpen && openSections.moderation} timeout="auto" unmountOnExit>
            {renderEntries(moderationEntries)}
          </Collapse>
        </>
      ) : null}

      {searchEntries.length > 0 ? (
        <>
          {renderSectionHeader('search', 'Поиск', <SearchIcon />)}
          <Collapse in={sidebarOpen && openSections.search} timeout="auto" unmountOnExit>
            {renderEntries(searchEntries)}
          </Collapse>
        </>
      ) : null}

      {analyticsEntries.length > 0 ? (
        <>
          {renderSectionHeader('analytics', 'Статистика', <BarChartIcon />)}
          <Collapse in={sidebarOpen && openSections.analytics} timeout="auto" unmountOnExit>
            {renderEntries(analyticsEntries)}
          </Collapse>
        </>
      ) : null}

      {adminEntries.length > 0 ? (
        <>
          {renderSectionHeader('admin', 'Настройки', <SettingsIcon />)}
          <Collapse in={sidebarOpen && openSections.admin} timeout="auto" unmountOnExit>
            {renderEntries(adminEntries)}
          </Collapse>
        </>
      ) : null}
    </Menu>
  );
};

const CustomSidebar = (props: any) => (
  <Sidebar
    {...props}
    sx={{
      width: 280,
      '& .RaSidebar-drawerPaper': { width: 280 },
    }}
  >
    <CustomMenu />
  </Sidebar>
);

const CustomUserMenu = () => {
  const { identity } = useGetIdentity();
  const currentUser = (identity ?? null) as { role?: AppRole } | null;
  const role = currentUser?.role;

  return (
    <UserMenu>
      <MenuItemLink
        to="/profile-settings"
        primaryText="Настройки профиля"
        leftIcon={<SettingsIcon fontSize="small" />}
      />
      {canAccessPage(role, 'adminUsers') ? (
        <MenuItemLink
          to="/adminUsers"
          primaryText="Управление пользователями"
          leftIcon={<ManageAccountsIcon fontSize="small" />}
        />
      ) : null}
      {role === 'manager-admin' ? (
        <MenuItemLink
          to="/expertTariffs"
          primaryText="Тарифы для экспертов"
          leftIcon={<CurrencyRubleIcon fontSize="small" />}
        />
      ) : null}
      {canAccessPage(role, 'expertSla') ? (
        <MenuItemLink
          to="/expertSla"
          primaryText="SLA экспертов"
          leftIcon={<AccessTimeIcon fontSize="small" />}
        />
      ) : null}
      <Logout />
    </UserMenu>
  );
};

const CustomAppBar = () => (
  <AppBar userMenu={<CustomUserMenu />} />
);

export const Layout = ({ children }: { children: ReactNode }) => (
  <RALayout sidebar={CustomSidebar} menu={CustomMenu} appBar={CustomAppBar}>
    {children}
    <CheckForApplicationUpdate />
  </RALayout>
);
